/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/class/KavenLiteEvent.ts
 * @create:      2019-11-15 10:02:57.709
 * @modify:      2025-06-30 17:54:25.094
 * @version:     6.0.0
 * @times:       27
 * @lines:       101
 * @copyright:   Copyright © 2019-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { IKavenLiteEvent } from "../interface/IKavenLiteEvent";
import { Handler } from "../type/basic";

/**
 *
 * @since 4.0.0
 * @version 2022-06-26
 */
export class KavenLiteEvent<D, S> implements IKavenLiteEvent<D, S> {
    private handlers = new Map<symbol, Handler<D, S>>();

    public on(handler: Handler<D, S>): symbol {
        const key = Symbol();
        this.handlers.set(key, handler);
        return key;
    }

    public off(handler: Handler<D, S>): boolean;
    public off(key: symbol): boolean;
    public off(value: symbol | Handler<D, S>): boolean {
        if (typeof value === "symbol") {
            return this.handlers.delete(value);
        }

        const keys: symbol[] = [];
        this.handlers.forEach((v, k) => {
            if (v === value) {
                keys.push(k);
            }
        });

        if (keys.length > 0) {
            for (const key of keys) {
                this.handlers.delete(key);
            }
            
            return true;
        }

        return false;
    }

    public async trigger(data: D, sender: S): Promise<number> {
        if (this.Count === 0) {
            return 0;
        }

        const items = Array.from(this.handlers.values());
        const list: Promise<void>[] = [];
        for (const h of items) {
            const promise = h(data, sender);
            if (promise) {
                list.push(promise);
            }
        }

        await Promise.all(list);
        return items.length;
    }

    public expose(): IKavenLiteEvent<D, S> {
        return this;
    }

    public get Count() {
        return this.handlers.size;
    }
}
